import {
  ArrowUpRight,
  ArrowDownRight,
  ChartLine,
  ChartPie,
  Download,
  Info,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Registration } from "@/utils/types/NPS/Stepper/GetRegistrationDetils";
import GetPensionFundManagerService from "@/services/NPS/Stepper/APIs/GetPensionFundManagerService";
import { SchemeOption } from "@/utils/types/NPS/Stepper/PensionFundManager";
import BaseTable from "@/components/BaseTable/BaseTable";
import NavUserDetailsService from "@/services/NPS/NpsUser/APIs/NavUserDetailsService";
import { getTenantId } from "@/lib/storeManager";
import { useQuery } from "@tanstack/react-query";
import { NavUserDetailsArray } from "@/utils/types/NavUserDetils";
import { ProColumns } from "@ant-design/pro-components";
import Select from "react-select";
import DownloadBase64Excel from "@/components/downloadBase64Excel";
import { format } from "date-fns";
import DatePickerInput from "@/projects/NPS/Stepper/common/DatePickerInput";

const PerformanceCard = ({
  label,
  value,
  change,
  percent,
  colorDot,
  isPositive,
}: {
  label: string;
  value: string;
  change: string;
  percent: string;
  colorDot: string;
  isPositive: boolean;
}) => (
  <div
    className={`rounded-2xl border bg-card text-card-foreground shadow-sm transition-shadow hover:shadow-md ${
      isPositive ? "border-green-200" : "border-red-200"
    }`}
  >
    <div className="p-5">
      <div className="mb-3 flex items-center gap-2">
        <div className={`h-3 w-3 rounded-full ${colorDot}`} />
        <span className="text-sm font-medium text-secondary">{label}</span>
      </div>

      <p className="text-2xl font-bold text-secondary">{value}</p>

      <div
        className={`mt-2 flex items-center gap-1 ${
          isPositive ? "text-green-600" : "text-red-600"
        }`}
      >
        {isPositive ? (
          <ArrowUpRight className="h-4 w-4" />
        ) : (
          <ArrowDownRight className="h-4 w-4" />
        )}
        <span className="text-sm font-medium">
          {change} ({percent})
        </span>
      </div>

      <p className="mt-2 text-xs text-muted-foreground">As on 08 Dec 2025</p>
    </div>
  </div>
);

const selectStyles = {
  control: (base: any) => ({
    ...base,
    minHeight: "40px",
    borderRadius: "8px",
    borderColor: "var(--border)",
    paddingLeft: "4px",
    boxShadow: "none",
    "&:hover": { borderColor: "var(--border)" },
    backgroundColor: "var(--background)",
  }),
  menu: (base: any) => ({
    ...base,
    zIndex: 9999,
  }),
  option: (base: any, { isSelected }: any) => ({
    ...base,
    backgroundColor: isSelected ? "hsl(var(--primary)/0.1)" : "white",
    color: isSelected ? "hsl(var(--primary))" : "black",
    "&:hover": { backgroundColor: "hsl(var(--primary)/0.1)" },
  }),
};

const NavPerformanceTab: React.FC<{
  registrationDetails: Registration | null;
}> = ({ registrationDetails }) => {
  const tenantId = getTenantId() ?? "";
  const [schemeOptions, setSchemeOptions] = useState<SchemeOption[]>([]);
  const [schemesLoaded, setSchemesLoaded] = useState(false);
  const [schemeId, setSchemeId] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState<Date | null>(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    return date;
  });

  const [toDate, setToDate] = useState<Date | null>(new Date());

  useEffect(() => {
    const fetchSchemes = async () => {
      try {
        const response = await GetPensionFundManagerService.getSchemeByPfmId(
          registrationDetails?.schemeDtls?.[0]?.schemeList?.[0]?.pfmId ?? ""
        );

        if (response.success && response.options?.length > 0) {
          const firstSchemeId = response.options[0].value ?? null;
          // Only update if actually changed
          if (schemeId !== firstSchemeId) {
            setSchemeOptions(response.options);
            setSchemeId(firstSchemeId);
          }
          setSchemesLoaded(true);
        }
      } catch (error) {
        console.error(error);
      }
    };

    if (registrationDetails) {
      fetchSchemes();
    }
  }, [registrationDetails]);

  const isQueryReady = schemesLoaded && !!schemeId && !!fromDate && !!toDate;

  const formattedFromDate = fromDate ? format(fromDate, "dd/MM/yyyy") : "";

  const formattedToDate = toDate ? format(toDate, "dd/MM/yyyy") : "";

  const { data, isLoading } = useQuery({
    queryKey: [
      "navUserDetails",
      tenantId,
      schemeId,
      formattedFromDate,
      formattedToDate,
    ],
    queryFn: () =>
      NavUserDetailsService.getNavUserDetails(
        formattedFromDate,
        formattedToDate,
        schemeId!,
        "N"
      ),
    enabled: isQueryReady,
  });

  const handleExport = async () => {
    try {
      const response = await NavUserDetailsService.getNavUserDetails(
        format(fromDate!, "dd/MM/yyyy"),
        format(toDate!, "dd/MM/yyyy"),
        schemeId!,
        "Y"
      );

      if (response.success && response.navDetails?.buffer) {
        DownloadBase64Excel(response.navDetails.buffer, "NAV_Details.xlsx");
      }
    } catch (error) {
      console.error("Export failed", error);
    }
  };

  const columns: ProColumns<NavUserDetailsArray>[] = [
    {
      title: "Date",
      dataIndex: "bussinessDate",
      key: "bussinessDate",
    },
    {
      title: "Scheme Name",
      dataIndex: "schemeName",
      key: "schemeName",
    },
    {
      title: "PFM Name",
      dataIndex: "pfmName",
      key: "tierType",
    },
    {
      title: "NAV",
      dataIndex: "nav",
      key: "nav",
    },
  ];
  return (
    <div className="mt-6 space-y-6">
      {/* ================= TOP PERFORMANCE ================= */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <PerformanceCard
          label="Equity (E)"
          value="₹58.42"
          change="+0.85"
          percent="1.47%"
          colorDot="bg-blue-500"
          isPositive
        />

        <PerformanceCard
          label="Corporate Bond (C)"
          value="₹42.18"
          change="+0.12"
          percent="0.29%"
          colorDot="bg-green-500"
          isPositive
        />

        <PerformanceCard
          label="Government Bond (G)"
          value="₹35.67"
          change="+0.08"
          percent="0.22%"
          colorDot="bg-amber-500"
          isPositive
        />

        <PerformanceCard
          label="Alternative (A)"
          value="₹28.95"
          change="-0.15"
          percent="-0.52%"
          colorDot="bg-purple-500"
          isPositive
        />
      </div>

      {/* ================= NAV DETAILS ================= */}
      <div className="rounded-2xl border border-border/60 bg-card shadow-sm transition-shadow hover:shadow-md">
        <div className="flex items-center justify-between p-6 pb-3">
          <h3 className="flex items-center gap-2 text-lg font-bold">
            <ChartLine className="h-5 w-5 text-primary" />
            NAV Details – HDFC Pension Fund Management Limited
          </h3>

          <div className="flex items-center gap-3">
            {/* <label className="mb-1 font-nunito text-[16px] font-semibold text-[#707070]">
                From Date
                <span className="text-red-500">*</span>
              </label> */}
            <DatePickerInput
              value={fromDate ? format(fromDate, "dd/MM/yyyy") : ""}
              onChange={(value: string) => {
                // Try to parse the input string into a Date and update state
                // Assuming value in "dd/MM/yyyy" format
                if (value) {
                  const [day, month, year] = value.split("/").map(Number);
                  const date = new Date(year, month - 1, day);
                  if (!isNaN(date.getTime())) {
                    setFromDate(date);
                  } else {
                    setFromDate(null);
                  }
                } else {
                  setFromDate(null);
                }
              }}
              // className="rounded-md border px-3 py-1 text-sm w-full"
            />
            {/* <label className="mb-1 font-nunito text-[16px] font-semibold text-[#707070]">
                To Date
                <span className="text-red-500">*</span>
              </label> */}
            <DatePickerInput
              value={toDate ? format(toDate, "dd/MM/yyyy") : ""}
              onChange={(value: string) => {
                // Try to parse the input string into a Date and update state
                // Assuming value in "dd/MM/yyyy" format
                if (value) {
                  const [day, month, year] = value.split("/").map(Number);
                  const date = new Date(year, month - 1, day);
                  if (!isNaN(date.getTime())) {
                    setToDate(date);
                  } else {
                    setToDate(null);
                  }
                } else {
                  setToDate(null);
                }
              }}
              // className="rounded-md border px-3 py-1 text-sm w-72"
            />

            <Select
              className="w-full"
              value={
                schemeId
                  ? {
                      value: schemeId,
                      label:
                        schemeOptions.find(option => option.value === schemeId)
                          ?.label ?? "",
                    }
                  : null
              }
              styles={selectStyles}
              options={schemeOptions.map(option => ({
                value: option.value,
                label: option.label,
              }))}
              onChange={(option: any) => setSchemeId(option?.value ?? null)}
            />

            {/* Export Button */}
            <button
              className="flex items-center gap-2 rounded-lg border px-6 py-3 text-xs hover:bg-muted/50"
              onClick={handleExport}
            >
              <Download className="h-3 w-3" />
              Export
            </button>
          </div>
        </div>

        {/* TABLE */}
        <div className="overflow-x-auto p-6 pt-0">
          <BaseTable<NavUserDetailsArray>
            loading={isLoading}
            columns={columns}
            dataSource={
              (data?.navDetails?.navDetails ?? []) as NavUserDetailsArray[]
            }
            total={10}
            onPageChange={() => {}}
            rowKey="bussinessDate"
            page={1}
            pageSize={10}
            headerTitle=""
            toolBarRender={undefined}
            // request={() => refetchNavUserDetails()}
          />

          {/* INFO */}
          <div className="mt-4 flex items-start gap-2 rounded-lg bg-blue-50 p-3">
            <Info className="h-4 w-4 shrink-0 text-blue-600" />
            <p className="text-sm text-blue-700">
              NAV is updated at the end of each business day. PFM manages
              investments across asset classes.
            </p>
          </div>
        </div>
      </div>

      {/* ================= UNIT HOLDINGS ================= */}
      <div className="rounded-2xl border border-border/60 bg-card shadow-sm">
        <div className="p-6 pb-3">
          <h3 className="flex items-center gap-2 text-base font-bold">
            <ChartPie className="h-5 w-5 text-primary" />
            Unit Holdings Summary
          </h3>
        </div>

        <div className="space-y-4 p-6 pt-0">
          {[
            {
              label: "Equity (E)",
              units: "767.6179",
              nav: "₹48.83",
              value: "₹37,482.16",
            },
            {
              label: "Corporate Bond (C)",
              units: "417.7656",
              nav: "₹28.57",
              value: "₹11,936.98",
            },
            {
              label: "Government Bond (G)",
              units: "906.8500",
              nav: "₹28.22",
              value: "₹25,590.21",
            },
          ].map(i => (
            <div key={i.label} className="rounded-xl bg-muted/30 p-4">
              <div className="mb-2 font-medium text-secondary">{i.label}</div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Units</p>
                  <p className="font-mono font-bold">{i.units}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">NAV</p>
                  <p className="font-bold">{i.nav}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Value</p>
                  <p className="font-bold text-primary">{i.value}</p>
                </div>
              </div>
            </div>
          ))}

          <div className="rounded-xl border border-primary/20 bg-primary/10 p-4">
            <div className="flex justify-between font-semibold">
              <span>Total Units</span>
              <span className="font-mono">2092.2335</span>
            </div>
            <div className="mt-2 flex justify-between text-lg font-bold text-primary">
              <span>Total Value</span>
              <span>₹75,009.35</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavPerformanceTab;
